import type { Locale } from "@/lib/i18n";

export type FaqItem = { q: Record<Locale, string>; a: Record<Locale, string> };

/** Sık sorulan sorular — anasayfa özeti ve /sss sayfası aynı kaynağı kullanır. */
export const FAQ_ITEMS: FaqItem[] = [
  {
    q: { tr: "oravity'ye nasıl geçerim?", en: "How do I switch to oravity?" },
    a: {
      tr: "Mevcut yazılımınızdaki hasta, randevu ve finans verilerini oravity'ye biz aktarıyoruz. Ekibiniz kısa bir eğitimin ardından aynı gün kullanmaya başlayabilir.",
      en: "We migrate your patient, appointment and finance data from your current software. Your team can start the same day after a short onboarding.",
    },
  },
  {
    q: { tr: "Verilerim güvende mi?", en: "Is my data secure?" },
    a: {
      tr: "Her kliniğin verisi izole bir alanda saklanır ve KVKK'ya uygun işlenir. Yetkilendirme ve güvenlik modülüyle kimin neye eriştiği tümüyle kontrol altındadır.",
      en: "Each clinic's data is stored in an isolated space and processed per Turkey's KVKK. The roles & security module keeps every access under control.",
    },
  },
  {
    q: {
      tr: "Mevzuata uyumlu mu (E-Nabız, E-Reçete, e-Fatura)?",
      en: "Is it compliant (E-Nabız, e-prescription, e-invoice)?",
    },
    a: {
      tr: "Evet. E-Nabız, E-Reçete ve e-Fatura entegrasyonları hazır gelir; klinik süreçleri mevzuata uygun biçimde tasarlanmıştır.",
      en: "Yes. E-Nabız, e-prescription and e-invoice integrations are built in, and clinical flows are designed to be compliant.",
    },
  },
  {
    q: { tr: "Çoklu şubeyi destekliyor mu?", en: "Does it support multiple branches?" },
    a: {
      tr: "Tek muayenehaneden zincir kliniğe kadar ölçeklenir. Tüm şubeleri, personeli ve finansı tek bir panelden yönetebilirsiniz.",
      en: "It scales from a single practice to a chain. You manage all branches, staff and finance from one panel.",
    },
  },
  {
    q: {
      tr: "Bulut tabanlı mı, kurulum gerekiyor mu?",
      en: "Is it cloud-based? Do I need to install anything?",
    },
    a: {
      tr: "oravity buluttan çalışır; herhangi bir kurulum yapmadan tarayıcıdan erişirsiniz. Güncellemeler otomatik gelir, sunucu bakımıyla uğraşmazsınız.",
      en: "oravity runs in the cloud; you access it from a browser with no installation. Updates arrive automatically — no server maintenance.",
    },
  },
  {
    q: { tr: "Demo alabilir miyim?", en: "Can I get a demo?" },
    a: {
      tr: "Elbette. Demo talep formunu doldurun; ekibimiz kliniğinizin ihtiyaçlarına göre özel bir demo planlasın.",
      en: "Of course. Fill in the demo request form and our team will schedule a demo tailored to your clinic.",
    },
  },
  {
    q: { tr: "Kurulum ve destek nasıl sağlanıyor?", en: "How are onboarding and support handled?" },
    a: {
      tr: "Kurulum, veri aktarımı ve eğitim ekibimiz tarafından yapılır. Sonrasında da destek ekibimiz ve site üzerindeki AI asistan Ora 7/24 yanınızda olur.",
      en: "Setup, migration and training are handled by our team. Afterwards our support team and the on-site AI assistant Ora is with you 24/7.",
    },
  },
  {
    q: { tr: "Mobil ve tabletten erişebilir miyim?", en: "Can I access it from mobile and tablet?" },
    a: {
      tr: "Evet. oravity buluttan çalıştığı için bilgisayar, tablet ve telefondan tarayıcı üzerinden erişebilirsiniz; ayrı bir kurulum gerekmez.",
      en: "Yes. Because oravity is cloud-based, you can reach it from a computer, tablet or phone via the browser — no separate install needed.",
    },
  },
  {
    q: {
      tr: "Birden fazla kullanıcı ve yetki tanımlayabilir miyim?",
      en: "Can I define multiple users and permission levels?",
    },
    a: {
      tr: "Evet. Hekim, asistan, resepsiyon ve yönetici gibi rollere ayrı yetkiler tanımlar, her kullanıcının neyi görüp değiştirebileceğini kontrol edersiniz.",
      en: "Yes. You assign separate permissions to roles such as dentist, assistant, front desk and manager, controlling what each user can see and change.",
    },
  },
  {
    q: { tr: "Fiyatlandırma nasıl?", en: "How does pricing work?" },
    a: {
      tr: "oravity, hekim sayınıza göre kademeli paketlerle fiyatlandırılır; ek modülleri ihtiyaç duydukça eklersiniz. Aşım ücreti yoktur; kurulum, veri aktarımı ve eğitim fiyata dahildir. Güncel paket ve modül fiyatları (KDV hariç) Fiyatlandırma sayfasındadır.",
      en: "oravity is priced in tiers by your number of dentists, and you add modules only when you need them. There are no overage fees; setup, data migration and training are included. Current plan and module prices (excl. VAT) are on the Pricing page.",
    },
  },
];

/** SSS sayfasının yolu (TR: /sss, EN: /en/faq). */
export function faqPath(locale: Locale): string {
  return locale === "en" ? "/en/faq" : "/sss";
}

/** FAQPage JSON-LD (verilen locale + öğe kümesine göre). */
export function buildFaqJsonLd(
  locale: Locale,
  items: FaqItem[] = FAQ_ITEMS
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q[locale],
      acceptedAnswer: { "@type": "Answer", text: f.a[locale] },
    })),
  };
}
